import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, StatusBadge, fmtDate } from "@/components/shared";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Progress as ProgressBar } from "@/components/ui/progress";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  GraduationCap, Plus, ArrowRight, Award, BookOpen, ListChecks, Users, TrendingUp,
  BadgeCheck, Clock, AlertTriangle, PlayCircle, CheckCircle2,
} from "lucide-react";
import { toast } from "sonner";

const AUDIENCES = [
  { value: "trainer", label: "Trainer" }, { value: "location_manager", label: "Location Manager" },
  { value: "franchise_owner", label: "Franchise Owner" }, { value: "head_office", label: "Head Office" },
];
const STATUSES = [{ value: "draft", label: "Draft" }, { value: "active", label: "Active" }, { value: "archived", label: "Archived" }];
const emptyCourse = { name: "", audience: "trainer", certification_type: "", estimated_duration: "", description: "", status: "draft", required: false, valid_for_months: "" };

export default function Academy() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const role = user?.role;
  const isGlobal = ["super_admin", "head_office_admin"].includes(role);
  const isManager = ["franchise_owner", "location_manager"].includes(role);
  const [dash, setDash] = useState(null);
  const [courses, setCourses] = useState([]);
  const [team, setTeam] = useState([]);
  const [reports, setReports] = useState(null);
  const [err, setErr] = useState(false);
  const [loadErr, setLoadErr] = useState(false);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(emptyCourse);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    try {
      const d = await api.get("/academy/dashboard"); setDash(d.data);
      if (d.data.scope !== "trainer") {
        const [c, t, r] = await Promise.all([api.get("/academy/courses"), api.get("/academy/team"), api.get("/academy/reports")]);
        setCourses(c.data); setTeam(t.data); setReports(r.data);
      }
    } catch (e) {
      if (e?.response?.status === 403) setErr(true); else setLoadErr(true);
    }
  };
  useEffect(() => { load(); }, []);

  const create = async () => {
    setSaving(true);
    try {
      const payload = { ...form, valid_for_months: form.valid_for_months ? Number(form.valid_for_months) : null };
      const r = await api.post("/academy/courses", payload);
      toast.success("Course created"); setOpen(false); setForm(emptyCourse);
      navigate(`/academy/courses/${r.data.id}`);
    } catch { toast.error("Failed to create course"); }
    setSaving(false);
  };

  if (err) return <div className="text-center py-20 text-muted-foreground" data-testid="academy-no-access">You don't have access to HV Academy.</div>;
  if (loadErr) return <div className="text-center py-20 text-muted-foreground" data-testid="academy-load-error">Couldn't load HV Academy. Please refresh to try again.</div>;
  if (!dash) return <div className="text-center py-20 text-muted-foreground">Loading...</div>;

  if (dash.scope === "trainer") return <TrainerAcademy dash={dash} navigate={navigate} />;

  return (
    <div>
      <PageHeader
        title="HV Academy"
        subtitle="Onboard, train and certify the network so every location delivers the Hundeverket method to the same standard."
        action={isGlobal && <Button onClick={() => { setForm(emptyCourse); setOpen(true); }} className="gap-2" data-testid="academy-new-course"><Plus size={16} strokeWidth={1.5} /> New Course</Button>}
      />

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-6">
        <Stat icon={GraduationCap} n={dash.total_courses} label="Total Courses" sub={`${dash.active_courses} active`} />
        <Stat icon={Users} n={dash.trainers_enrolled} label="Trainers Enrolled" />
        <Stat icon={BadgeCheck} n={dash.trainers_certified} label="Certified" tone="text-green-700" />
        <Stat icon={Clock} n={dash.trainers_in_progress} label="In Progress" tone="text-amber-700" />
        <Stat icon={AlertTriangle} n={dash.trainers_not_certified} label="Not Certified" tone="text-red-700" />
        <Stat icon={Award} n={dash.franchise_owners_onboarded} label="Owners Onboarded" />
        <Stat icon={AlertTriangle} n={dash.expiring_soon} label="Expiring Soon" tone="text-orange-700" />
        <Stat icon={TrendingUp} n={`${dash.completion_rate}%`} label="Completion Rate" tone="text-emerald-700" />
      </div>

      <Tabs defaultValue="courses">
        <TabsList className="h-auto">
          <TabsTrigger value="courses" data-testid="academy-tab-courses">Courses <span className="ml-1.5 text-xs text-muted-foreground">({courses.length})</span></TabsTrigger>
          <TabsTrigger value="team" data-testid="academy-tab-team">Team <span className="ml-1.5 text-xs text-muted-foreground">({team.length})</span></TabsTrigger>
          <TabsTrigger value="reports" data-testid="academy-tab-reports">Reports</TabsTrigger>
        </TabsList>

        <TabsContent value="courses" className="mt-4">
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
            {courses.map((c) => <CourseCard key={c.id} c={c} onClick={() => navigate(`/academy/courses/${c.id}`)} />)}
            {courses.length === 0 && <p className="text-muted-foreground col-span-full text-center py-12">No academy courses yet.</p>}
          </div>
        </TabsContent>

        <TabsContent value="team" className="mt-4">
          <Card className="border-border">
            <CardContent className="p-0">
              <table className="w-full text-sm">
                <thead className="border-b border-border text-xs uppercase tracking-wide text-muted-foreground">
                  <tr>
                    <th className="text-left font-semibold px-4 py-3">Trainer</th>
                    <th className="text-left font-semibold px-4 py-3 hidden sm:table-cell">Location</th>
                    <th className="text-left font-semibold px-4 py-3">Certification</th>
                    <th className="text-left font-semibold px-4 py-3 w-40">Progress</th>
                    <th className="text-left font-semibold px-4 py-3 hidden lg:table-cell">Required Missing</th>
                  </tr>
                </thead>
                <tbody>
                  {team.map((t) => (
                    <tr key={t.id} className="border-b border-border/50 hover:bg-muted/40 cursor-pointer" onClick={() => navigate(`/trainers/${t.id}`)} data-testid="academy-team-row">
                      <td className="px-4 py-3 font-medium">{t.full_name}</td>
                      <td className="px-4 py-3 hidden sm:table-cell text-muted-foreground">{t.location_name || "—"}</td>
                      <td className="px-4 py-3"><StatusBadge value={t.cert_status} /></td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2"><ProgressBar value={t.avg_progress} className="h-1.5 flex-1" /><span className="text-xs text-muted-foreground w-9 text-right">{t.avg_progress}%</span></div>
                      </td>
                      <td className="px-4 py-3 hidden lg:table-cell text-xs text-muted-foreground">{t.required_missing?.length ? t.required_missing.join(", ") : <span className="text-green-700">All complete</span>}</td>
                    </tr>
                  ))}
                  {team.length === 0 && <tr><td colSpan={5} className="text-center text-muted-foreground py-10">No trainers in scope.</td></tr>}
                </tbody>
              </table>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="reports" className="mt-4">
          {reports && (
            <div className="grid lg:grid-cols-2 gap-4">
              <Card className="border-border"><CardHeader><CardTitle className="font-heading text-base flex items-center gap-2"><BadgeCheck size={16} /> Trainers by Certification</CardTitle></CardHeader>
                <CardContent className="space-y-2">
                  {reports.trainers_by_certification.map((x) => (
                    <div key={x.name} className="flex items-center justify-between" data-testid="academy-report-cert"><StatusBadge value={x.name} /><span className="font-medium">{x.value}</span></div>
                  ))}
                  {reports.trainers_by_certification.length === 0 && <p className="text-sm text-muted-foreground py-4 text-center">No enrollments yet.</p>}
                </CardContent>
              </Card>
              <Card className="border-border"><CardHeader><CardTitle className="font-heading text-base flex items-center gap-2"><TrendingUp size={16} /> Completion by Location</CardTitle></CardHeader>
                <CardContent className="space-y-3">
                  {reports.by_location.map((l) => (
                    <div key={l.name} data-testid="academy-report-loc">
                      <div className="flex justify-between text-xs text-muted-foreground mb-1"><span>{l.name}</span><span>{l.completed}/{l.total} · {l.rate}%</span></div>
                      <ProgressBar value={l.rate} className="h-1.5" />
                    </div>
                  ))}
                  {reports.by_location.length === 0 && <p className="text-sm text-muted-foreground py-4 text-center">No data.</p>}
                </CardContent>
              </Card>
              <Card className="border-border"><CardHeader><CardTitle className="font-heading text-base flex items-center gap-2"><AlertTriangle size={16} className="text-amber-600" /> Required Courses Incomplete</CardTitle></CardHeader>
                <CardContent className="space-y-2">
                  {reports.required_incomplete.map((x) => (
                    <div key={`${x.name}-${x.course}`} className="flex items-center justify-between text-sm" data-testid="academy-report-incomplete">
                      <div><div className="font-medium">{x.name}</div><div className="text-xs text-muted-foreground">{x.course}</div></div>
                      <span className="text-xs text-muted-foreground">{x.progress}%</span>
                    </div>
                  ))}
                  {reports.required_incomplete.length === 0 && <p className="text-sm text-muted-foreground py-4 text-center">All required courses complete.</p>}
                </CardContent>
              </Card>
              <Card className="border-border"><CardHeader><CardTitle className="font-heading text-base flex items-center gap-2"><AlertTriangle size={16} className="text-red-600" /> Expired Certifications</CardTitle></CardHeader>
                <CardContent className="space-y-2">
                  {reports.expired.map((x) => (
                    <div key={`${x.name}-${x.course}`} className="flex items-center justify-between text-sm" data-testid="academy-report-expired"><span className="font-medium">{x.name}</span><span className="text-xs text-muted-foreground">{x.course}</span></div>
                  ))}
                  {reports.expired.length === 0 && <p className="text-sm text-muted-foreground py-4 text-center">No expired certifications.</p>}
                </CardContent>
              </Card>
            </div>
          )}
        </TabsContent>
      </Tabs>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-heading">New Academy Course</DialogTitle><DialogDescription>Create the course shell — add modules, lessons and the certification checklist next.</DialogDescription></DialogHeader>
          <div className="grid gap-4 py-2">
            <div className="grid gap-1.5"><Label>Course name</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="course-field-name" /></div>
            <div className="grid grid-cols-2 gap-4">
              <div className="grid gap-1.5"><Label>Audience</Label>
                <Select value={form.audience} onValueChange={(v) => setForm({ ...form, audience: v })}><SelectTrigger data-testid="course-field-audience"><SelectValue /></SelectTrigger>
                  <SelectContent>{AUDIENCES.map((a) => <SelectItem key={a.value} value={a.value}>{a.label}</SelectItem>)}</SelectContent></Select>
              </div>
              <div className="grid gap-1.5"><Label>Certification type</Label><Input value={form.certification_type} onChange={(e) => setForm({ ...form, certification_type: e.target.value })} placeholder="e.g. HV Certified Trainer" data-testid="course-field-cert" /></div>
            </div>
            <div className="grid gap-1.5"><Label>Description</Label><Textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} /></div>
            <div className="grid grid-cols-3 gap-4">
              <div className="grid gap-1.5"><Label>Duration</Label><Input value={form.estimated_duration} onChange={(e) => setForm({ ...form, estimated_duration: e.target.value })} placeholder="6 weeks" /></div>
              <div className="grid gap-1.5"><Label>Status</Label>
                <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}><SelectTrigger data-testid="course-field-status"><SelectValue /></SelectTrigger>
                  <SelectContent>{STATUSES.map((s) => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}</SelectContent></Select>
              </div>
              <div className="grid gap-1.5"><Label>Valid (months)</Label><Input type="number" value={form.valid_for_months} onChange={(e) => setForm({ ...form, valid_for_months: e.target.value })} placeholder="24" /></div>
            </div>
            <div className="flex items-center justify-between rounded-lg border border-border/60 p-3">
              <Label>Required for role</Label>
              <Switch checked={form.required} onCheckedChange={(v) => setForm({ ...form, required: v })} data-testid="course-field-required" />
            </div>
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button><Button onClick={create} disabled={saving || !form.name} data-testid="course-save-btn">{saving ? "Creating..." : "Create course"}</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function CourseCard({ c, onClick }) {
  return (
    <Card className="border-border hover:-translate-y-0.5 hover:shadow-md transition-all cursor-pointer group" onClick={onClick} data-testid="academy-course-card">
      <CardContent className="p-5">
        <div className="flex items-start justify-between">
          <div className="flex items-center gap-2.5">
            <div className="h-10 w-10 rounded-xl bg-neutral-900 flex items-center justify-center"><GraduationCap size={20} className="text-primary-foreground" strokeWidth={1.5} /></div>
            <div>
              <div className="font-heading font-semibold text-base leading-tight">{c.name}</div>
              <div className="flex items-center gap-1.5 mt-1"><StatusBadge value={c.status} />{c.required && <span className="text-[10px] font-semibold px-2 py-0.5 rounded-full bg-primary/10 text-primary">Required</span>}</div>
            </div>
          </div>
          <ArrowRight size={18} className="text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity" />
        </div>
        <p className="text-sm text-muted-foreground mt-3 line-clamp-2">{c.description || "No description."}</p>
        <div className="grid grid-cols-3 gap-2 mt-4 text-center">
          <MiniStat icon={BookOpen} n={c.lessons_count} label="Lessons" />
          <MiniStat icon={ListChecks} n={c.checklist_count} label="Checklist" />
          <MiniStat icon={Users} n={c.enrolled_count} label="Enrolled" />
        </div>
        <div className="flex items-center justify-between mt-4 pt-3 border-t border-border/60 text-xs text-muted-foreground">
          <span className="capitalize">{String(c.audience).replace(/_/g, " ")}</span>
          <span className="flex items-center gap-1 text-green-700"><BadgeCheck size={12} /> {c.certified_count} certified</span>
        </div>
      </CardContent>
    </Card>
  );
}

const Stat = ({ icon: Icon, n, label, sub, tone }) => (
  <Card className="border-border"><CardContent className="p-4">
    <div className="flex items-center gap-2 text-muted-foreground mb-1.5"><Icon size={15} strokeWidth={1.5} /><span className="text-[11px] uppercase tracking-wide font-semibold">{label}</span></div>
    <div className={`text-2xl font-heading font-semibold tracking-tight ${tone || ""}`}>{n}</div>
    {sub && <div className="text-[11px] text-muted-foreground mt-0.5">{sub}</div>}
  </CardContent></Card>
);

const MiniStat = ({ icon: Icon, n, label }) => (
  <div className="rounded-lg bg-muted/50 py-2">
    <div className="flex items-center justify-center gap-1 text-foreground"><Icon size={13} className="text-primary" /> <span className="font-heading font-semibold">{n ?? 0}</span></div>
    <div className="text-[10px] uppercase tracking-wide text-muted-foreground mt-0.5">{label}</div>
  </div>
);

function TrainerAcademy({ dash, navigate }) {
  const next = dash.next_required;
  return (
    <div>
      <PageHeader title="My Academy" subtitle="Your assigned Hundeverket courses and certification progress." />

      <div className="grid lg:grid-cols-4 gap-4 mb-6">
        <Card className="border-border lg:col-span-1 bg-neutral-900 text-primary-foreground">
          <CardContent className="p-5">
            <div className="flex items-center gap-2 text-xs uppercase tracking-wide font-semibold opacity-80"><Award size={14} /> Certification</div>
            <div className="text-2xl font-heading font-semibold mt-2" data-testid="trainer-cert-status">{dash.certified ? "Certified" : dash.in_progress ? "In Progress" : "Not Started"}</div>
            <div className="mt-1 text-sm opacity-80">{dash.completed}/{dash.assigned} courses completed</div>
          </CardContent>
        </Card>
        <div className="lg:col-span-3 grid grid-cols-3 gap-4">
          <Stat icon={BookOpen} n={dash.assigned} label="Assigned" />
          <Stat icon={Clock} n={dash.in_progress} label="In Progress" tone="text-amber-700" />
          <Stat icon={CheckCircle2} n={dash.completed} label="Completed" tone="text-green-700" />
        </div>
      </div>

      {next && (
        <Card className="border-border bg-secondary/40 mb-6"><CardContent className="p-4 flex items-center justify-between">
          <div><div className="text-[11px] uppercase tracking-wide font-semibold text-muted-foreground">Next Required Course</div><div className="font-medium mt-0.5">{next.course_name}</div></div>
          <Button className="gap-2" onClick={() => navigate(`/academy/enrollments/${next.id}`)} data-testid="academy-continue-next"><PlayCircle size={16} /> {next.status === "not_started" ? "Start" : "Continue"}</Button>
        </CardContent></Card>
      )}

      <h2 className="font-heading text-lg font-semibold mb-3">Assigned Courses</h2>
      <div className="grid md:grid-cols-2 gap-4">
        {dash.enrollments.map((e) => (
          <Card key={e.id} className="border-border hover:shadow-md transition-all cursor-pointer" onClick={() => navigate(`/academy/enrollments/${e.id}`)} data-testid="academy-my-course">
            <CardContent className="p-5">
              <div className="flex items-start justify-between">
                <div className="flex items-center gap-2.5">
                  <div className="h-10 w-10 rounded-xl bg-neutral-900 flex items-center justify-center"><GraduationCap size={20} className="text-primary-foreground" strokeWidth={1.5} /></div>
                  <div><div className="font-heading font-semibold">{e.course_name}</div><div className="text-xs text-muted-foreground">{e.certification_type} · {e.estimated_duration}</div></div>
                </div>
                <StatusBadge value={e.certification_status} />
              </div>
              <div className="mt-4"><div className="flex justify-between text-xs text-muted-foreground mb-1"><span>Progress</span><span>{e.progress_percentage}%</span></div><ProgressBar value={e.progress_percentage} className="h-2" /></div>
              {e.expires_at && e.status === "completed" && <div className="text-xs text-muted-foreground mt-2">Valid until {fmtDate(e.expires_at)}</div>}
            </CardContent>
          </Card>
        ))}
        {dash.enrollments.length === 0 && <p className="text-muted-foreground col-span-full text-center py-12">No courses assigned yet. Your Head Office will enrol you soon.</p>}
      </div>
    </div>
  );
}
